import React, { useState } from 'react';
import { FileText, AlertTriangle, Scale, Search, BookOpen, Download, Home, Upload, File, X } from 'lucide-react';

const TrademarkRejectionApp = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [officeActionText, setOfficeActionText] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file only.');
      return;
    }

    setUploadedFile(file);
    setExtracting(true);
    
    try {
      // In a real implementation, this would use a PDF parsing library
      // For demo purposes, we'll simulate PDF text extraction
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock extracted text that would come from PDF parsing
      const mockExtractedText = `
UNITED STATES PATENT AND TRADEMARK Office
OFFICE ACTION

Application Serial No.: 90123456
Mark: INNOVATE PRO
Filing Date: January 15, 2024

The examining attorney has reviewed the above-identified trademark application and determined that registration must be REFUSED for the following reason(s):

SECTION 2(d) REFUSAL - LIKELIHOOD OF CONFUSION

Registration is refused because the applied-for mark so resembles the mark in Registration No. 1234567 for INNOVATIVE PROFESSIONAL as to be likely, when used on or in connection with the identified goods, to cause confusion, or to cause mistake, or to deceive.

The marks are similar in appearance, sound, connotation, and overall commercial impression. Both marks contain the terms "INNOVAT-" and "PRO" which are the dominant elements of each mark. The goods are related and travel in the same channels of trade.

SECTION 2(e)(1) REFUSAL - MERELY DESCRIPTIVE

Registration is refused because the applied-for mark is merely descriptive of the goods. The mark INNOVATE PRO merely describes a characteristic, quality, function, or feature of the identified "business consulting services" because it describes services that are innovative and professional in nature.

DISCLAIMER REQUIRED

If the applicant submits an acceptable response to the refusal above, applicant must submit a disclaimer of the descriptive wording "PRO" because it is merely descriptive of the services.

SPECIMEN REFUSAL

The specimen is refused because it does not show the applied-for mark as actually used in commerce on or in connection with the services. The submitted webpage screenshot shows only advertising of the services and not actual use of the mark in the sale or advertising of the services.

Response due within 6 months of the mailing date of this Office Action.
      `;
      
      setOfficeActionText(mockExtractedText.trim());
      setExtracting(false);
      
    } catch (error) {
      console.error('Error extracting PDF text:', error);
      alert('Error extracting text from PDF. Please try again or contact support.');
      setExtracting(false);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setOfficeActionText('');
  };

  const disclaimer = "Please note that all assessment results are preliminary and may not be confidential if not using a paid AI account with the proper safeguards so that AI will not train on the data. The app is provided free of charge and there is no attorney client relationship by using this app. Further, proper creation and capture of IP value require tailored legal and valuation strategies—founders should consult a qualified patent attorney at PatentPC.com to develop a robust IP portfolio and maximize startup valuation. Please visit PatentPC.com to consult with a patent lawyer.";

  const analyzeOfficeAction = () => {
    if (!officeActionText.trim()) {
      alert('Please upload an Office Action PDF or enter text before analyzing.');
      return;
    }

    setLoading(true);
    
    // Simulate analysis processing
    setTimeout(() => {
      const mockAnalysis = generateMockAnalysis(officeActionText);
      setAnalysis(mockAnalysis);
      setLoading(false);
      setCurrentPage('analysis');
    }, 2000);
  };

  const generateMockAnalysis = (text) => {
    // This would be replaced with actual AI analysis in a real implementation
    const hasLikelihoodOfConfusion = text.toLowerCase().includes('likelihood of confusion') || text.toLowerCase().includes('2(d)');
    const hasDescriptiveness = text.toLowerCase().includes('descriptive') || text.toLowerCase().includes('2(e)');
    const hasSpecimenIssue = text.toLowerCase().includes('specimen') || text.toLowerCase().includes('use in commerce');
    const hasDisclaimer = text.toLowerCase().includes('disclaimer');

    return {
      issues: [
        ...(hasLikelihoodOfConfusion ? [{
          type: 'Likelihood of Confusion (Section 2(d))',
          severity: 'High',
          description: 'The examining attorney has refused registration based on likelihood of confusion with a prior registered mark.',
          legalBasis: 'Trademark Act Section 2(d), 15 U.S.C. §1052(d)',
          strategies: [
            'Conduct comprehensive search of cited registrations and analyze differences in marks, goods/services, and channels of trade',
            'Argue differences in appearance, sound, meaning, and commercial impression of the marks',
            'Present evidence of peaceful coexistence in the marketplace',
            'Consider amending the identification of goods/services to avoid conflict',
            'Evaluate possibility of consent agreement with prior registrant'
          ],
          caselaw: 'In re E.I. DuPont DeNemours & Co., 476 F.2d 1357 (C.C.P.A. 1973) - establishes the DuPont factors for likelihood of confusion analysis'
        }] : []),
        ...(hasDescriptiveness ? [{
          type: 'Descriptiveness Refusal (Section 2(e)(1))',
          severity: 'Medium',
          description: 'The mark has been refused as merely descriptive of the goods/services.',
          legalBasis: 'Trademark Act Section 2(e)(1), 15 U.S.C. §1052(e)(1)',
          strategies: [
            'Argue the mark is suggestive rather than merely descriptive',
            'Provide evidence of acquired distinctiveness through extensive use',
            'Submit consumer survey evidence showing secondary meaning',
            'File declarations from industry experts or consumers',
            'Consider amending to Principal Register based on Section 2(f) acquired distinctiveness'
          ],
          caselaw: 'In re Abcor Development Corp., 588 F.2d 811 (C.C.P.A. 1978) - test for descriptiveness vs. suggestiveness'
        }] : []),
        ...(hasSpecimenIssue ? [{
          type: 'Specimen Refusal',
          severity: 'Medium',
          description: 'The submitted specimen does not show proper trademark use in commerce.',
          legalBasis: 'Trademark Act Section 1(a), 15 U.S.C. §1051(a); 37 C.F.R. §2.56',
          strategies: [
            'Submit a substitute specimen showing proper use of the mark on or in connection with the goods/services',
            'Provide webpage screenshots showing the mark in use in commerce',
            'Include packaging, labels, or advertising materials displaying the mark',
            'File declaration explaining the nature of use if specimen context is unclear'
          ],
          caselaw: 'In re Sones, 590 F.3d 1282 (Fed. Cir. 2009) - requirements for acceptable specimens'
        }] : []),
        ...(hasDisclaimer ? [{
          type: 'Disclaimer Requirement',
          severity: 'Low',
          description: 'The examining attorney requires a disclaimer of certain descriptive or generic terms.',
          legalBasis: 'Trademark Act Section 6(a), 15 U.S.C. §1056(a); TMEP §1213',
          strategies: [
            'Enter the requested disclaimer to proceed with registration',
            'Argue that the term is not merely descriptive in connection with the goods/services',
            'Provide evidence that the term has acquired distinctiveness',
            'Consider the impact of disclaimer on enforcement and scope of protection'
          ],
          caselaw: 'In re National Shooting Sports Foundation, Inc., 219 USPQ 1018 (TTAB 1983)'
        }] : [])
      ],
      recommendations: {
        immediate: [
          'Review all cited registrations and prior art thoroughly',
          'Gather evidence supporting registrability arguments',
          'Consider engaging trademark search firm for comprehensive analysis'
        ],
        strategic: [
          'Evaluate cost-benefit of various response strategies',
          'Consider filing continuation applications if amendments are needed',
          'Assess international filing strategy implications'
        ],
        timeline: 'Response due within 6 months of Office Action mailing date. Consider filing earlier to allow time for examiner review and potential final refusal.'
      }
    };
  };

  const DisclaimerBanner = () => (
    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
      <div className="flex">
        <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-medium mb-2">Legal Disclaimer</p>
          <p className="text-xs leading-relaxed">{disclaimer}</p>
        </div>
      </div>
    </div>
  );

  const Navigation = () => (
    <nav className="bg-blue-900 text-white p-4 mb-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center">
          <Scale className="mr-2" />
          Trademark Rejection Response Assistant
        </h1>
        <div className="flex space-x-4">
          <button 
            onClick={() => setCurrentPage('home')}
            className={`px-3 py-1 rounded flex items-center ${currentPage === 'home' ? 'bg-blue-700' : 'hover:bg-blue-800'}`}
          >
            <Home className="mr-1 h-4 w-4" />
            Home
          </button>
          <button 
            onClick={() => setCurrentPage('analyzer')}
            className={`px-3 py-1 rounded flex items-center ${currentPage === 'analyzer' ? 'bg-blue-700' : 'hover:bg-blue-800'}`}
          >
            <FileText className="mr-1 h-4 w-4" />
            Analyzer
          </button>
          <button 
            onClick={() => setCurrentPage('resources')}
            className={`px-3 py-1 rounded flex items-center ${currentPage === 'resources' ? 'bg-blue-700' : 'hover:bg-blue-800'}`}
          >
            <BookOpen className="mr-1 h-4 w-4" />
            Resources
          </button>
        </div>
      </div>
    </nav>
  );

  const HomePage = () => (
    <div className="max-w-4xl mx-auto">
      <DisclaimerBanner />
      
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <Scale className="mx-auto h-16 w-16 text-blue-600 mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Trademark Office Action Response Assistant</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Professional analysis and response strategies for USPTO trademark Office Actions. 
            Get expert guidance on overcoming rejections and requirements.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="text-center p-6 border rounded-lg hover:shadow-md transition-shadow">
            <Upload className="mx-auto h-12 w-12 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Upload PDF Documents</h3>
            <p className="text-gray-600">Simply upload your USPTO Office Action PDF for instant analysis</p>
          </div>
          
          <div className="text-center p-6 border rounded-lg hover:shadow-md transition-shadow">
            <Search className="mx-auto h-12 w-12 text-green-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Response Strategies</h3>
            <p className="text-gray-600">Get specific, actionable strategies for each type of refusal or requirement</p>
          </div>
          
          <div className="text-center p-6 border rounded-lg hover:shadow-md transition-shadow">
            <BookOpen className="mx-auto h-12 w-12 text-purple-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Legal Resources</h3>
            <p className="text-gray-600">Access relevant case law, TMEP sections, and examiner guidance</p>
          </div>
        </div>

        <div className="text-center">
          <button 
            onClick={() => setCurrentPage('analyzer')}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Start Analysis
          </button>
        </div>
      </div>
    </div>
  );

  const AnalyzerPage = () => (
    <div className="max-w-6xl mx-auto">
      <DisclaimerBanner />
      
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <FileText className="mr-3 text-blue-600" />
          Office Action Analyzer
        </h2>
        
        {/* File Upload Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Upload Office Action PDF</h3>
          
          {!uploadedFile ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <div className="mb-4">
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 inline-block">
                    Choose PDF File
                  </span>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-gray-500 text-sm">
                Upload the complete USPTO Office Action PDF document
              </p>
              <p className="text-gray-400 text-xs mt-2">
                Supported format: PDF only • Max file size: 10MB
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <File className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <p className="font-medium text-blue-900">{uploadedFile.name}</p>
                    <p className="text-sm text-blue-700">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={removeFile}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Remove file"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {extracting && (
                <div className="mt-4 flex items-center text-blue-700">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Extracting text from PDF...
                </div>
              )}
            </div>
          )}
        </div>

        {/* Alternative Text Input */}
        <div className="mb-6">
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Or Enter Office Action Text Manually</h3>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Paste the complete Office Action text below:
            </label>
            <textarea
              value={officeActionText}
              onChange={(e) => setOfficeActionText(e.target.value)}
              className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Paste the complete text of the USPTO Office Action here, including all refusals, requirements, and cited registrations..."
            />
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {uploadedFile ? `PDF uploaded: ${uploadedFile.name}` : `Characters: ${officeActionText.length}`}
          </div>
          <button
            onClick={analyzeOfficeAction}
            disabled={loading || extracting || !officeActionText.trim()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Analyzing...
              </>
            ) : extracting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processing PDF...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Analyze Office Action
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const AnalysisPage = () => {
    if (!analysis) return null;

    return (
      <div className="max-w-6xl mx-auto">
        <DisclaimerBanner />
        
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold flex items-center">
              <FileText className="mr-3 text-blue-600" />
              Office Action Analysis & Response Strategies
            </h2>
            <div className="flex space-x-2">
              <button 
                onClick={() => setCurrentPage('analyzer')}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
              >
                New Analysis
              </button>
              <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center">
                <Download className="mr-2 h-4 w-4" />
                Export Report
              </button>
            </div>
          </div>

          {/* Issues Analysis */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">1. Substantive Issues Identified</h3>
            {analysis.issues.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800">No major substantive issues detected in the provided Office Action text.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {analysis.issues.map((issue, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-lg font-semibold text-gray-900">{issue.type}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        issue.severity === 'High' ? 'bg-red-100 text-red-800' :
                        issue.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {issue.severity} Priority
                      </span>
                    </div>
                    
                    <div className="mb-4">
                      <h5 className="font-medium text-gray-700 mb-2">Issue Summary:</h5>
                      <p className="text-gray-600">{issue.description}</p>
                    </div>
                    
                    <div className="mb-4">
                      <h5 className="font-medium text-gray-700 mb-2">Legal Basis:</h5>
                      <p className="text-gray-600 font-mono text-sm">{issue.legalBasis}</p>
                    </div>
                    
                    <div className="mb-4">
                      <h5 className="font-medium text-gray-700 mb-2">Response Strategies:</h5>
                      <ul className="list-disc list-inside space-y-1 text-gray-600">
                        {issue.strategies.map((strategy, idx) => (
                          <li key={idx}>{strategy}</li>
                        ))}
                      </ul>
                    </div>
                    
                    {issue.caselaw && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <h6 className="font-medium text-blue-900 mb-1">Relevant Case Law:</h6>
                        <p className="text-blue-800 text-sm">{issue.caselaw}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recommendations */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">2. Strategic Recommendations</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-3">Immediate Actions</h4>
                <ul className="space-y-2">
                  {analysis.recommendations.immediate.map((action, idx) => (
                    <li key={idx} className="text-blue-800 text-sm flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-3">Strategic Considerations</h4>
                <ul className="space-y-2">
                  {analysis.recommendations.strategic.map((consideration, idx) => (
                    <li key={idx} className="text-green-800 text-sm flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      {consideration}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-900 mb-2">Timeline Considerations</h4>
              <p className="text-amber-800 text-sm">{analysis.recommendations.timeline}</p>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">3. Recommended Next Steps</h3>
            <div className="space-y-3">
              <div className="flex items-center p-3 bg-white rounded border">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <div className="ml-4">
                  <p className="text-gray-900 font-medium">Gather Supporting Evidence</p>
                  <p className="text-gray-600 text-sm">Collect all necessary documentation, specimens, and evidence to support your response arguments.</p>
                </div>
              </div>
              
              <div className="flex items-center p-3 bg-white rounded border">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <div className="ml-4">
                  <p className="text-gray-900 font-medium">Draft Comprehensive Response</p>
                  <p className="text-gray-600 text-sm">Prepare a detailed response addressing each issue with specific legal arguments and evidence.</p>
                </div>
              </div>
              
              <div className="flex items-center p-3 bg-white rounded border">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <div className="ml-4">
                  <p className="text-gray-900 font-medium">Consider Professional Consultation</p>
                  <p className="text-gray-600 text-sm">For complex issues, consider consulting with a qualified trademark attorney at PatentPC.com for specialized guidance.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ResourcesPage = () => (
    <div className="max-w-4xl mx-auto">
      <DisclaimerBanner />
      
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <BookOpen className="mr-3 text-blue-600" />
          Trademark Law Resources
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Key Statutory Provisions</h3>
            <div className="space-y-3">
              <div className="border rounded p-3">
                <h4 className="font-medium">Section 2(d) - Likelihood of Confusion</h4>
                <p className="text-sm text-gray-600 mt-1">15 U.S.C. §1052(d) - Refusal based on likelihood of confusion with prior marks</p>
              </div>
              <div className="border rounded p-3">
                <h4 className="font-medium">Section 2(e) - Descriptiveness</h4>
                <p className="text-sm text-gray-600 mt-1">15 U.S.C. §1052(e) - Refusal of merely descriptive marks</p>
              </div>
              <div className="border rounded p-3">
                <h4 className="font-medium">Section 1(a) - Use in Commerce</h4>
                <p className="text-sm text-gray-600 mt-1">15 U.S.C. §1051(a) - Requirements for use-based applications</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Important Case Law</h3>
            <div className="space-y-3">
              <div className="border rounded p-3">
                <h4 className="font-medium">DuPont Factors</h4>
                <p className="text-sm text-gray-600 mt-1">In re E.I. DuPont DeNemours & Co., 476 F.2d 1357 (C.C.P.A. 1973)</p>
              </div>
              <div className="border rounded p-3">
                <h4 className="font-medium">Descriptiveness Test</h4>
                <p className="text-sm text-gray-600 mt-1">In re Abcor Development Corp., 588 F.2d 811 (C.C.P.A. 1978)</p>
              </div>
              <div className="border rounded p-3">
                <h4 className="font-medium">Specimen Requirements</h4>
                <p className="text-sm text-gray-600 mt-1">In re Sones, 590 F.3d 1282 (Fed. Cir. 2009)</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Additional Resources</h3>
          <ul className="space-y-2 text-blue-800">
            <li>• Trademark Manual of Examining Procedure (TMEP)</li>
            <li>• USPTO Trademark Trial and Appeal Board Manual of Procedure (TBMP)</li>
            <li>• Federal Circuit and TTAB Decisions Database</li>
            <li>• USPTO Trademark Electronic Search System (TESS)</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-6">
        {currentPage === 'home' && <HomePage />}
        {currentPage === 'analyzer' && <AnalyzerPage />}
        {currentPage === 'analysis' && <AnalysisPage />}
        {currentPage === 'resources' && <ResourcesPage />}
      </div>
      
      <footer className="bg-gray-800 text-white text-center py-4 mt-12">
        <p className="text-sm">
          For professional trademark prosecution services, visit{' '}
          <span className="text-blue-400 font-semibold">PatentPC.com</span>
        </p>
      </footer>
    </div>
  );
};

export default TrademarkRejectionApp;